/* ===== routes.ts — جدول المسارات وخريطة العناوين =====
   الرموز مطابقة حرفياً لما تستخدمه النسخة الأصلية في App.route (‎#plan‎ · ‎#uat‎ …).

   التحويل إلى صفحة واحدة (انظر docs/agents/integ.md «التحويل إلى صفحة واحدة»):
   الأصل كان ثلاث صفحات HTML والمسار في الـhash (Alber-PM-Platform.html#plan).
   الآن ملف HTML واحد والصفحة والمسار من المسار النظيف نفسه:
     /login            ← صفحة الدخول (Login.html في الأصل)
     /sign?t=<رمز>     ← بوابة التوقيع العامة (Sign.html في الأصل)
     /<مسار>           ← التطبيق: /dashboard · /plan · /uat …
   تعارض الاسم «sign»: للتطبيق مسار داخلي 'sign' (مركز التواقيع، تبويب «التواقيع»)
   وللبوابة العامة اسم Sign.html. البوابة تأخذ /sign لأن رابطها يُرسل بالبريد إلى
   موقّعين من خارج المنصة (أقصر وأوضح)، ومركز التواقيع يأخذ /signatures. الاسم
   الداخلي App.route يبقى 'sign' كما هو — الخريطة في العنوان وحده.

   لا شيء هنا يقرأ الـhash إلا تحويل الروابط القديمة «/#plan» ← «/plan» (مسار
   معروف بالضبط، في الجذر وحده) — فشظايا المصادقة (#access_token=… · ?code=…)
   لا تُفهم مساراً أبداً وتبقى لـ supabase-js يقرؤها ثم يمسحها بنفسه.

   النسخة الثانية (v2) تحت الأساس ‎/v2‎ في المستند نفسه (docs/v2/analysis.md §0.3):
     /v2 · /v2/<مسار>  ← تطبيق v2 (مركز التواقيع ‎/v2/signatures‎ كما في v1)
     /v2/login         ← صفحة دخول v2 (نوع صفحة مستقلّ: ‎/login‎ في v1 مضبوط بصرياً)
   أسماء المسارات الداخلية هي نفسها (App.route · routeNeeds لا يتغيّران)، والأساس وحده
   يتغيّر: يضبطه تطبيق v2 عند إقلاعه (setRouteBase('/v2'))، والافتراضي '' = v1 حرفياً —
   كل دالة هنا بالأساس '' تُرجع ما كانت تُرجعه قبل v2 بايتاً ببايت. */

import { PM_CONFIG } from '@/lib/config';

export const ROUTES = [
  'dashboard',
  'registry',
  'users',
  'project',
  'wizard',
  'plan',
  'track',
  'audit',
  'uat',
  'sign',
  'notifications',
  'settings',
] as const;

export type Route = (typeof ROUTES)[number];

export const DEFAULT_ROUTE: Route = 'dashboard';

const isRoute = (r: string): r is Route => (ROUTES as readonly string[]).includes(r);

/** مسار غير معروف يسقط إلى الرئيسية — نفس سلوك `else` في App.render الأصلية. */
export function normalizeRoute(raw: string | null | undefined): Route {
  const r = (raw || '').replace(/^[#/]/, '');
  return isRoute(r) ? r : DEFAULT_ROUTE;
}

/* ===== الصفحات ===== */

export type PageKind = 'app' | 'login' | 'sign' | 'v2' | 'v2login';

export const V2_BASE = '/v2';
export type RouteBase = '' | '/v2';

/* أساس عناوين التطبيق الجاري في هذا المستند ('' = v1). */
let base: RouteBase = '';
/* (v2 وحده) استعلام المسار التالي — v2 لا يحمل استعلام صفحة إلى التي تليها. */
let nextQuery = '';

export function setRouteBase(b: RouteBase): void {
  base = b;
}

export function routeBase(): RouteBase {
  return base;
}

/** نوع صفحة التطبيق الجاري: 'app' لـ v1 · 'v2' تحت ‎/v2‎. */
export function appPageKind(): 'app' | 'v2' {
  return base === V2_BASE ? 'v2' : 'app';
}

/** الاستعلام الذي يُلحق بعنوان المسار في App.go. v1 (الأساس ''): الاستعلام الحالي كما هو
 *  (كما كان يبقى مع تغيّر الـhash وحده). v2: ما ضبطه setNextQuery ثم يُفرَّغ، وإلا لا شيء. */
export function navQuery(currentSearch: string): string {
  if (base === '') return currentSearch;
  const q = nextQuery;
  nextQuery = '';
  return q;
}

export function setNextQuery(q: string): void {
  nextQuery = q;
}

/** صفحة دخول التطبيق الجاري: ‎/login‎ (v1) · ‎/v2/login‎ (v2)، ومعها الاستعلام كما هو. */
export function loginPath(query = ''): string {
  return base + '/' + PM_CONFIG.routes.login + query;
}

/** باقي العنوان تحت الأساس b، أو null إن لم يكن تحته. «تحته» = العنوان هو الأساس نفسه أو
 *  يبدأ بـ «الأساس/» — فـ‎/v2x‎ ليس تحت ‎/v2‎. الأساس '' يحوي كل عنوان. */
function underBase(pathname: string, b: string): string | null {
  if (!b) return pathname;
  if (pathname === b) return '';
  return pathname.startsWith(b + '/') ? pathname.slice(b.length) : null;
}

/** العنوان بلا الأساس الجاري؛ وما ليس تحته يبقى كما هو (رابط v1 ‎/plan‎ داخل v2). */
function stripBase(pathname: string): string {
  const rest = underBase(pathname, base);
  return rest === null ? pathname : rest;
}

/** مركز التواقيع في التطبيق: /signatures (لأن /sign للبوابة العامة). */
const ROUTE_SEGMENT: Partial<Record<Route, string>> = { sign: 'signatures' };
const SEGMENT_ROUTE: Record<string, Route> = { signatures: 'sign' };

/** أيّ صفحة يرسمها هذا المسار. ‎/v2‎ وما تحته لـ v2 (و ‎/v2/login‎ دخولها) أيّاً كان الأساس؛
 *  وكل ما ليس /login ولا /sign فهو التطبيق — ومنه المسار المجهول الذي يرسم الرئيسية بلا
 *  تبويب مضاء كالأصل. */
export function pageOf(pathname: string): PageKind {
  const v2 = underBase(pathname, V2_BASE);
  if (v2 !== null) return v2 === '/' + PM_CONFIG.routes.login ? 'v2login' : 'v2';
  const seg = pathname.replace(/^\//, '');
  if (seg === PM_CONFIG.routes.login) return 'login';
  if (seg === PM_CONFIG.routes.sign) return 'sign';
  return 'app';
}

/** عنوان مسار التطبيق: 'plan' ⇒ '/plan' · 'sign' ⇒ '/signatures' (وتحت v2: ‎/v2/plan‎ …).
 *  نصّ خام لغير المعروف. */
export function routePath(r: string): string {
  return base + '/' + ((isRoute(r) && ROUTE_SEGMENT[r]) || r);
}

/** App.route من المسار — نصّ خام كالأصل `(location.hash||'').replace('#','')||'dashboard'`:
 *  '/' ⇒ 'dashboard' · '/signatures' ⇒ 'sign' · '/foo' ⇒ 'foo' (تُرسم الرئيسية بلا تبويب). */
export function routeFromPath(pathname: string): string {
  const seg = stripBase(pathname).replace(/^\//, '');
  return SEGMENT_ROUTE[seg] || seg || DEFAULT_ROUTE;
}

/** مسار التطبيق المعروف لهذا العنوان، أو null لصفحات الدخول/التوقيع أو مسار مجهول.
 *  تحت v2 يُفهم عنوان v1 أيضاً (‎/plan‎ في رابط إشعار قديم ⇐ 'plan' يُفتح داخل v2). */
export function knownRouteOfPath(pathname: string): Route | null {
  const kind = pageOf(pathname);
  if (kind !== 'app' && kind !== 'v2') return null;
  const r = routeFromPath(pathname);
  return isRoute(r) ? r : null;
}

/** App.route الحالي من شريط العنوان (نصّ خام — انظر routeFromPath). */
export function routeFromLocation(): string {
  return routeFromPath(window.location.pathname);
}

/** رابط قديم بالـhash في الجذر: «/#plan» ⇒ «/plan» (مع الاستعلام كما هو).
 *  مسار معروف بالضبط فقط — فـ«/#access_token=…» وأي شظية أخرى لا تُمسّ. */
export function legacyHashTarget(loc: { pathname: string; search: string; hash: string }): string | null {
  if (loc.pathname !== '/' || !loc.hash) return null;
  const r = loc.hash.replace(/^#/, '');
  return isRoute(r) ? routePath(r) + loc.search : null;
}
